import { useState, useRef, useEffect, useCallback } from 'react'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { Bar } from 'react-chartjs-2'
import { useDashReport } from '../hooks/useDashReport'
import type { SegmentacionReport } from '../types'

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend)

const COLORES: Record<number, { bg: string; border: string }> = {
  0: { bg: 'rgba(249, 115, 22, 0.85)', border: '#f97316' },
  1: { bg: 'rgba(251, 146, 60, 0.85)', border: '#fb923c' },
  2: { bg: 'rgba(253, 186, 116, 0.85)', border: '#fdba74' },
}

const CARD_COLORS = ['#f97316', '#fb923c', '#fdba74']

const LOGO_URL = '/logo-tonal-negro.png'

export function EurofarmaSegmento1604Page() {
  const { data, isLoading, error } = useDashReport<SegmentacionReport>('eurofarma/segmento-1604')
  const [filtro, setFiltro] = useState('todas')
  const [expanded, setExpanded] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const filtrarEspecialidad = useCallback((especialidad: string) => {
    setFiltro(especialidad)
  }, [])

  useEffect(() => {
    if (contentRef.current) {
      if (expanded) {
        contentRef.current.style.maxHeight = contentRef.current.scrollHeight + 'px'
        contentRef.current.style.opacity = '1'
      } else {
        contentRef.current.style.maxHeight = '0px'
        contentRef.current.style.opacity = '0'
      }
    }
  }, [expanded, filtro])

  if (isLoading) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-gray-100 p-4 lg:p-6 min-h-screen">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center py-32">
            <p className="text-red-500">Error al cargar el reporte. Intente nuevamente.</p>
          </div>
        </div>
      </div>
    )
  }

  const segmentos = data.segmentos
  const totalGeneral = data.total_general

  const segmentosFiltrados = filtro === 'todas'
    ? segmentos
    : segmentos.filter(s => s.especialidad === filtro)

  const tituloResumen = filtro === 'todas' ? 'Resumen General' : filtro
  const labelTotal = filtro === 'todas' ? 'Total Base de Datos' : filtro
  const totalMostrado = filtro === 'todas'
    ? totalGeneral
    : (segmentos.find(s => s.especialidad === filtro)?.total ?? 0)

  const labels = segmentosFiltrados.map(s => s.especialidad)
  const valores = segmentosFiltrados.map(s => s.total)
  const bgColors = segmentosFiltrados.map((_s, idx) => COLORES[idx]?.bg || COLORES[0].bg)
  const borderColors = segmentosFiltrados.map((_s, idx) => COLORES[idx]?.border || COLORES[0].border)

  const chartData = {
    labels,
    datasets: [{
      label: 'Total',
      data: valores,
      backgroundColor: bgColors,
      borderColor: borderColors,
      borderWidth: 1,
    }],
  }

  const chartOptions = {
    indexAxis: 'y' as const,
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { right: 80 },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: 'end' as const,
        align: 'right' as const,
        offset: 4,
        clip: false,
        color: '#6b7280',
        font: { size: 11, weight: 500 },
        formatter: function (value: number) {
          return value.toLocaleString('es-EC')
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        grace: '5%',
        ticks: {
          callback: function (value: number | string) {
            return Number(value).toLocaleString('es-EC')
          },
        },
      },
    },
  }

  return (
    <>
      <style>{`
        @keyframes pulse-subtle {
          0%, 100% { transform: scale(1); opacity: 0.7; }
          50% { transform: scale(1.1); opacity: 1; }
        }
        .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
        .animate-pulse-subtle.expanded { animation: none; opacity: 1; transform: scale(1); }
        .filter-btn.active {
          background-color: #f97316 !important;
          color: white !important;
        }
        .filter-btn.active:hover {
          background-color: #ea580c !important;
        }
        .filter-btn:not(.active):hover {
          background-color: #e5e7eb;
        }
      `}</style>
      <div className="bg-gray-100 p-4 lg:p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center">
                <img src={LOGO_URL} alt="Tonal" className="h-8 lg:h-10 w-auto mr-4 lg:mr-6" />
                <div>
                  <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-1">{data.titulo}</h1>
                  <p className="text-sm lg:text-base text-gray-600">{data.subtitulo}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href="https://enterprise.tonalstrategies.com/pricing/" target="_blank" rel="noreferrer"
                   className="inline-flex items-center px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 shadow-sm">
                  Calculador
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Filtro de Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                <span className="font-semibold text-gray-900">Filtrar por especialidad:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => filtrarEspecialidad('todas')}
                  className={`filter-btn ${filtro === 'todas' ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                >
                  Todas
                </button>
                {segmentos.map((seg, idx) => (
                  <button
                    key={idx}
                    onClick={() => filtrarEspecialidad(seg.especialidad)}
                    className={`filter-btn ${filtro === seg.especialidad ? 'active' : ''} px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 bg-gray-100 text-gray-700`}
                  >
                    {seg.especialidad}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Estadisticas Generales */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 lg:p-6 border border-orange-200 mb-6">
            <h2 className="font-semibold text-gray-900 mb-4 flex items-center text-lg">
              <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <span>{tituloResumen}</span>
            </h2>

            {/* Total General */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
              <div className="bg-white rounded-lg px-4 py-3 text-center shadow-sm sm:col-span-1">
                <p className="text-3xl lg:text-4xl font-bold text-gray-900">{totalMostrado.toLocaleString('es-EC')}</p>
                <p className="text-sm text-gray-600 mt-1">{labelTotal}</p>
              </div>
              {segmentos.map((seg, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-lg px-4 py-3 text-center shadow-sm"
                  style={{ display: filtro === 'todas' || filtro === seg.especialidad ? 'block' : 'none' }}
                >
                  <p className="text-2xl lg:text-3xl font-bold" style={{ color: CARD_COLORS[idx] || CARD_COLORS[0] }}>{seg.total.toLocaleString('es-EC')}</p>
                  <p className="text-sm text-gray-600 mt-1">{seg.especialidad}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{seg.porcentaje}%</p>
                </div>
              ))}
            </div>
          </div>

          {/* Graficas */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
              <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Distribución por Especialidad
              </h3>
              <div className="h-64 lg:h-80">
                <Bar data={chartData} options={chartOptions} plugins={[ChartDataLabels]} />
              </div>
            </div>
          </div>

          {/* Tabla de Detalle por Especialidad */}
          <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6 mb-6">
            <button
              onClick={() => setExpanded(!expanded)}
              className="w-full text-left focus:outline-none group py-3 hover:bg-gray-50 rounded-lg px-2 cursor-pointer"
            >
              <div className="font-semibold text-gray-900 flex items-center justify-between">
                <div className="flex items-center">
                  <svg className="w-5 h-5 text-orange-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <span className="text-base lg:text-lg">Detalle por Especialidad</span>
                  <span className="text-xs lg:text-sm text-gray-500 ml-2">({data.total_especialidades} especialidades)</span>
                </div>
                <svg
                  className={`w-5 h-5 text-gray-400 transform transition-transform duration-300 ${expanded ? 'expanded' : 'animate-pulse-subtle'}`}
                  style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </button>

            <div
              ref={contentRef}
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{ maxHeight: 0, opacity: 0 }}
            >
              <div className="overflow-x-auto mt-4">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Especialidad</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Porcentaje</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {segmentos.map((seg, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50"
                        style={{ display: filtro === 'todas' || filtro === seg.especialidad ? '' : 'none' }}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{seg.especialidad}</td>
                        <td className="px-4 py-3 text-sm text-right font-semibold text-orange-600">{seg.total.toLocaleString('es-EC')}</td>
                        <td className="px-4 py-3 text-sm text-right text-gray-500">{seg.porcentaje}%</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-4 py-3 text-sm text-gray-900">Total</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{data.total_general.toLocaleString('es-EC')}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">100%</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 text-center">
          <div className="text-xs text-gray-400">
            <a href="https://enterprise.tonalstrategies.com" className="hover:text-gray-600 transition-colors">
              Tonal Enterprise
            </a>
            <span className="mx-2">&bull;</span>
            <span>2026 Tonal Strategies</span>
          </div>
        </footer>
      </div>
    </>
  )
}
